import React, { useEffect, useState } from "react";
import { Loader2, Shield, Users as UsersIcon, BookOpen, GraduationCap, Gift, Ban, ArrowUpCircle, ArrowDownCircle, Save, Plus, X, Sparkles, LifeBuoy, Check, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { prettyDate, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "entries", label: "Entries", icon: BookOpen },
  { key: "programs", label: "Programs", icon: GraduationCap },
  { key: "resources", label: "Resources", icon: LifeBuoy },
];

const RES_CATEGORIES = ["treatment", "meetings", "family", "reading", "podcasts", "other"];

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/users"); setUsers(data.users || []); } catch (e) { toast.error("Could not load users"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e, "Action failed")); } };
  const gift = (id) => act(() => api.patch(`/admin/users/${id}/gift-premium`, { duration: "year" }), "Gifted 1 year premium");
  const ban = (u) => act(() => api.patch(`/admin/users/${u.id}/${u.banned ? "unban" : "ban"}`), u.banned ? "User unbanned" : "User banned");
  const role = (u) => act(() => api.patch(`/admin/users/${u.id}/${u.role === "admin" ? "demote" : "promote"}`), "Role updated");

  const filtered = users.filter((u) => !q || (u.name + u.email + (u.display_alias || "")).toLowerCase().includes(q.toLowerCase()));

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  return (
    <div>
      <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…"
        className="w-full mb-4 px-4 py-2.5 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
      <div className="space-y-2" data-testid="admin-users-list">
        {filtered.slice(0, 60).map((u) => (
          <div key={u.id} className="bg-card border border-border rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-semibold truncate">{u.name} {u.role === "admin" && <span className="text-xs text-brass">· admin</span>} {u.banned && <span className="text-xs text-destructive">· banned</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email} · {u.display_alias}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button data-testid={`gift-${u.id}`} onClick={() => gift(u.id)} title="Gift 1yr premium" className="w-9 h-9 rounded-full bg-secondary hover:bg-brass/20 text-brass flex items-center justify-center"><Gift className="w-4 h-4" /></button>
              <button onClick={() => role(u)} title="Promote/demote" className="w-9 h-9 rounded-full bg-secondary hover:bg-primary/20 text-primary flex items-center justify-center">{u.role === "admin" ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}</button>
              <button onClick={() => ban(u)} title="Ban/unban" className="w-9 h-9 rounded-full bg-secondary hover:bg-destructive/20 text-destructive flex items-center justify-center"><Ban className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EntriesTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regen, setRegen] = useState(false);
  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/entries"); setEntries(Array.isArray(data) ? data : []); } catch (e) { toast.error("Could not load entries"); } finally { setLoading(false); } })(); }, []);

  const open = async (date) => { try { const { data } = await api.get(`/admin/entries/${date}`); setEditing({ ...data, integration_questions_text: (data.integration_questions || []).join("\n") }); } catch (e) { toast.error("Could not load entry"); } };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/entries/${editing.date}`, {
        title: editing.title, quote: editing.quote, quote_author: editing.quote_author,
        reflection: editing.reflection, affirmation: editing.affirmation, month_theme: editing.month_theme,
        integration_questions: editing.integration_questions_text.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Entry saved"); setEditing(null);
    } catch (e) { toast.error("Could not save entry"); }
    finally { setSaving(false); }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate this reading with AI? This overwrites the current content on the live backend.")) return;
    setRegen(true);
    try {
      await api.post(`/admin/entries/${editing.date}/regenerate`);
      const { data } = await api.get(`/admin/entries/${editing.date}`);
      setEditing({ ...data, integration_questions_text: (data.integration_questions || []).join("\n") });
      toast.success("Reading regenerated with AI");
    } catch (e) {
      toast.error(errMsg(e, "Could not regenerate — the backend AI may be offline"));
    } finally { setRegen(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const inp = "w-full px-3 py-2 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm";
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="admin-entries-list">
        {entries.map((e) => (
          <button key={e.date} data-testid={`admin-entry-${e.date}`} onClick={() => open(e.date)}
            className="text-left bg-card border border-border rounded-xl p-3 hover:border-primary transition-colors">
            <p className="text-xs text-brass font-semibold">{prettyDate(e.date)}</p>
            <p className="text-sm line-clamp-2 mt-0.5">{e.title}</p>
          </button>
        ))}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-nightsea/75 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto bg-card border border-border rounded-2xl p-6 animate-fade-up">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-muted-foreground"><X className="w-5 h-5" /></button>
            <h3 className="font-heading text-xl font-semibold mb-4">Edit {prettyDate(editing.date)}</h3>
            <div className="space-y-3">
              <div><label className="text-xs text-muted-foreground">Title</label><input className={inp} value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="admin-entry-title" /></div>
              <div><label className="text-xs text-muted-foreground">Month theme</label><input className={inp} value={editing.month_theme || ""} onChange={(e) => setEditing({ ...editing, month_theme: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Quote</label><input className={inp} value={editing.quote || ""} onChange={(e) => setEditing({ ...editing, quote: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Quote author</label><input className={inp} value={editing.quote_author || ""} onChange={(e) => setEditing({ ...editing, quote_author: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Reflection</label><textarea rows={6} className={inp + " resize-none"} value={editing.reflection || ""} onChange={(e) => setEditing({ ...editing, reflection: e.target.value })} data-testid="admin-entry-reflection" /></div>
              <div><label className="text-xs text-muted-foreground">Affirmation</label><textarea rows={2} className={inp + " resize-none"} value={editing.affirmation || ""} onChange={(e) => setEditing({ ...editing, affirmation: e.target.value })} /></div>
              <div><label className="text-xs text-muted-foreground">Integration questions (one per line)</label><textarea rows={4} className={inp + " resize-none"} value={editing.integration_questions_text} onChange={(e) => setEditing({ ...editing, integration_questions_text: e.target.value })} /></div>
            </div>
            <div className="flex gap-2 mt-5">
              <button data-testid="admin-entry-regenerate" onClick={regenerate} disabled={regen || saving}
                className="flex-1 flex items-center justify-center gap-2 border border-brass/50 text-brass font-semibold py-2.5 rounded-full hover:bg-brass/10 transition-colors disabled:opacity-50">
                {regen ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Regenerate with AI
              </button>
              <button data-testid="admin-entry-save" onClick={save} disabled={saving || regen} className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-full disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProgramsTab = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: "", summary: "", duration_days: 7, category: "recovery", cover_emoji: "⚓", is_published: true });
  const [busy, setBusy] = useState(false);
  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/programs"); setPrograms(Array.isArray(data) ? data : data.programs || []); } catch (e) { toast.error("Could not load programs"); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/programs", { ...form, duration_days: parseInt(form.duration_days, 10) }); toast.success("Program created"); setShow(false); load(); }
    catch (err) { toast.error(errMsg(err, "Could not create")); }
    finally { setBusy(false); }
  };
  const inp = "w-full px-3 py-2 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm";
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  return (
    <div>
      <button data-testid="admin-new-program" onClick={() => setShow(true)} className="mb-4 flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-full"><Plus className="w-4 h-4" /> New program</button>
      <div className="space-y-2" data-testid="admin-programs-list">
        {programs.map((p) => (
          <div key={p.program_id || p.id} className="bg-card border border-border rounded-xl p-4">
            <p className="font-semibold">{p.cover_emoji || p.icon} {p.title}</p>
            <p className="text-xs text-muted-foreground">{p.category} · {p.duration_days} days · {p.is_published ? "published" : "draft"}</p>
          </div>
        ))}
      </div>
      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-nightsea/75 backdrop-blur-sm" onClick={() => setShow(false)} />
          <form onSubmit={create} className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 animate-fade-up">
            <h3 className="font-heading text-xl font-semibold mb-4">New program</h3>
            <div className="space-y-3">
              <input data-testid="program-title" className={inp} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <textarea className={inp + " resize-none"} rows={2} placeholder="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input className={inp} type="number" placeholder="Days" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} />
                <select data-testid="program-category" className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {["recovery","mental_health","mindfulness","relapse_prevention","trauma","family","spirituality","physical_health","other"].map((c) => (
                    <option key={c} value={c}>{c.replace(/_/g, " ")}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="w-4 h-4 accent-primary" /> Published</label>
            </div>
            <button data-testid="program-create-submit" type="submit" disabled={busy} className="mt-5 w-full bg-primary text-primary-foreground font-semibold py-2.5 rounded-full disabled:opacity-50">{busy ? "Creating…" : "Create"}</button>
          </form>
        </div>
      )}
    </div>
  );
};

const ResourcesTab = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ category: "treatment", title: "", description: "", url: "", phone: "" });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/resources"); setResources(data.resources || []); }
    catch (e) { toast.error("Could not load resources"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/resources", {
        category: form.category, title: form.title.trim(),
        description: form.description.trim() || null, url: form.url.trim() || null, phone: form.phone.trim() || null,
      });
      toast.success("Resource added"); setShow(false);
      setForm({ category: "treatment", title: "", description: "", url: "", phone: "" });
      load();
    } catch (err) { toast.error(errMsg(err, "Could not add resource")); }
    finally { setBusy(false); }
  };

  const approve = async (id) => { try { await api.patch(`/admin/resources/${id}/approve`); toast.success("Resource approved"); load(); } catch (e) { toast.error(errMsg(e, "Could not approve")); } };
  const remove = async (id) => { if (!window.confirm("Delete this resource?")) return; try { await api.delete(`/admin/resources/${id}`); toast.success("Resource deleted"); load(); } catch (e) { toast.error(errMsg(e, "Could not delete")); } };

  const inp = "w-full px-3 py-2 rounded-lg border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm";
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div>
      <button data-testid="admin-new-resource" onClick={() => setShow(true)} className="mb-4 flex items-center gap-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-2 rounded-full"><Plus className="w-4 h-4" /> Add resource</button>
      <div className="space-y-2" data-testid="admin-resources-list">
        {resources.map((r) => (
          <div key={r.id} data-testid="admin-resource-item" className="bg-card border border-border rounded-xl p-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-semibold flex items-center gap-2">
                {r.title}
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary capitalize">{r.category}</span>
                {r.approved ? <span className="text-xs text-seafoam">approved</span> : <span className="text-xs text-warning">pending</span>}
              </p>
              {r.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{r.description}</p>}
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary hover:underline"><ExternalLink className="w-3 h-3" /> link</a>}
                {r.phone && <span>{r.phone}</span>}
                {r.submitted_by_name && <span>by {r.submitted_by_name}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {!r.approved && <button data-testid={`approve-resource-${r.id}`} onClick={() => approve(r.id)} title="Approve" className="w-9 h-9 rounded-full bg-secondary hover:bg-seafoam/20 text-seafoam flex items-center justify-center"><Check className="w-4 h-4" /></button>}
              <button data-testid={`delete-resource-${r.id}`} onClick={() => remove(r.id)} title="Delete" className="w-9 h-9 rounded-full bg-secondary hover:bg-destructive/20 text-destructive flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {resources.length === 0 && <p className="text-muted-foreground py-6">No resources yet.</p>}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-nightsea/75 backdrop-blur-sm" onClick={() => setShow(false)} />
          <form onSubmit={create} className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6 animate-fade-up">
            <button type="button" onClick={() => setShow(false)} className="absolute top-4 right-4 text-muted-foreground"><X className="w-5 h-5" /></button>
            <h3 className="font-heading text-xl font-semibold mb-4">Add resource</h3>
            <div className="space-y-3">
              <input data-testid="resource-title" className={inp} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <select data-testid="resource-category" className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {RES_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea data-testid="resource-description" className={inp + " resize-none"} rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <input data-testid="resource-url" className={inp} placeholder="URL (optional)" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
              <input data-testid="resource-phone" className={inp} placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <button data-testid="resource-create-submit" type="submit" disabled={busy || !form.title.trim()} className="mt-5 w-full bg-primary text-primary-foreground font-semibold py-2.5 rounded-full disabled:opacity-50">{busy ? "Adding…" : "Add resource"}</button>
          </form>
        </div>
      )}
    </div>
  );
};

const Admin = () => {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState("users");
  if (!isAdmin) return (
    <div className="px-6 py-24 text-center">
      <Shield className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
      <p className="text-muted-foreground">Admins only. This area is restricted.</p>
    </div>
  );
  return (
    <div className="px-4 sm:px-6 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold flex items-center gap-2"><Shield className="w-7 h-7 text-brass" /> Admin Hub</h1>
        <p className="text-muted-foreground mt-2 mb-6">Manage users, daily entries, and programs.</p>
        <div className="flex gap-2 mb-6">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === key ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        {tab === "users" && <UsersTab />}
        {tab === "entries" && <EntriesTab />}
        {tab === "programs" && <ProgramsTab />}
        {tab === "resources" && <ResourcesTab />}
      </div>
    </div>
  );
};

export default Admin;
